import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Link as LinkIcon, TriangleAlert } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { getJSON, patchJSON } from '@/lib/api'

type Exposure = 'hidden' | 'members' | 'public'
type ExposureSetting = { key: string; label: string; exposure: Exposure; choices: Exposure[] }
type Profile = {
  member_directory_slug: string
  member_directory_listing?: { appears: boolean; reason: string | null }
  hide_from_member_directory: boolean
  public_directory_opt_in: boolean
  directory_field_exposures: ExposureSetting[]
}

const labels: Record<Exposure, string> = { hidden: 'Hidden', members: 'Members', public: 'Public' }

export function MemberPortalPrivacy() {
  const queryClient = useQueryClient()
  const profile = useQuery<{ profile: Profile }>({
    queryKey: ['member-portal-profile'],
    queryFn: () => getJSON('/api/v1/member_portal/profile'),
    retry: false,
  })
  const update = useMutation({
    mutationFn: (attributes: Record<string, unknown>) =>
      patchJSON<{ profile: Profile }>('/api/v1/member_portal/profile', { profile: attributes }),
    onSuccess: (data) => queryClient.setQueryData(['member-portal-profile'], data),
  })

  if (profile.isLoading)
    return <p className="mt-6 text-body-sm text-muted-foreground">Loading directory privacy...</p>
  const current = profile.data?.profile
  if (!current)
    return (
      <Alert className="mt-6">
        We couldn&apos;t load your directory privacy. Please try again.
      </Alert>
    )
  if (!current.member_directory_listing || !current.member_directory_slug)
    return <Alert className="mt-6">Directory privacy is available to members only.</Alert>
  const updateSwitch = (key: string, value: boolean) => update.mutate({ [key]: value })
  const updateExposure = (key: string, value: Exposure) =>
    update.mutate({ directory_field_exposures: { [key]: value } })
  const fellowPath = `/portal/directory/${current.member_directory_slug}`
  const publicPath = `/portal/profile/directory/preview?rendering=public`

  return (
    <div className="mt-3 space-y-3">
      <PrivacyHeader fellowPath={fellowPath} publicPath={publicPath} />
      <ListingStatus listing={current.member_directory_listing} />
      <ListingSwitches profile={current} updateSwitch={updateSwitch} />
      <ExposureFields fields={current.directory_field_exposures} updateExposure={updateExposure} />
    </div>
  )
}

function PrivacyHeader({ fellowPath, publicPath }: { fellowPath: string; publicPath: string }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="space-y-1">
        <Link
          to="/portal/profile"
          className="text-body-sm text-muted-foreground hover:text-foreground"
        >
          ← Profile
        </Link>
        <h1 className="text-subheading font-semibold">Directory listing</h1>
      </div>
      <div className="flex shrink-0 gap-2">
        <Button asChild type="button" variant="outline" size="sm">
          <Link to={fellowPath}>
            <LinkIcon className="mr-2 size-4" />
            View as a fellow member
          </Link>
        </Button>
        <Button asChild type="button" variant="outline" size="sm">
          <a href={publicPath}>
            <LinkIcon className="mr-2 size-4" />
            View as the public
          </a>
        </Button>
      </div>
    </div>
  )
}

function ListingStatus({ listing }: { listing: NonNullable<Profile['member_directory_listing']> }) {
  const listed = listing.appears
  return (
    <div
      className={
        listed
          ? 'flex items-start gap-2 rounded-md border border-success/30 bg-success/10 px-3 py-2 text-body-sm text-success'
          : 'flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-body-sm'
      }
    >
      {listed ? (
        <Check className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      ) : (
        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
      )}
      <div className="space-y-0.5">
        <p>
          {listed
            ? "You're listed in the member directory."
            : "You're not listed in the member directory."}
        </p>
        {listing.reason && <p>{listing.reason}</p>}
      </div>
    </div>
  )
}

function ListingSwitches({
  profile,
  updateSwitch,
}: {
  profile: Profile
  updateSwitch: (key: string, value: boolean) => void
}) {
  return (
    <section className="overflow-hidden rounded border border-border bg-card">
      <h2 className="border-b border-border px-5 py-3 text-body font-medium">
        Where you&apos;re listed
      </h2>
      <PrivacySwitch
        label="List me in the member directory"
        checked={!profile.hide_from_member_directory}
        onChange={(value) => updateSwitch('hide_from_member_directory', !value)}
      />
      <PrivacySwitch
        label="Also list me on the association's public website"
        checked={profile.public_directory_opt_in}
        onChange={(value) => updateSwitch('public_directory_opt_in', value)}
        divided
      />
      {profile.public_directory_opt_in && (
        <p className="border-t border-border px-5 py-2 text-caption text-muted-foreground">
          Search engines keep a copy for a while after this is turned off.
        </p>
      )}
    </section>
  )
}

function ExposureFields({
  fields,
  updateExposure,
}: {
  fields: ExposureSetting[]
  updateExposure: (key: string, value: Exposure) => void
}) {
  return (
    <section className="overflow-hidden rounded border border-border bg-card">
      <h2 className="border-b border-border px-5 py-3 text-body font-medium">
        What people can see about you
      </h2>
      {fields.map((field, index) => (
        <div
          key={field.key}
          className={`flex items-center justify-between gap-4 px-5 py-2 ${index > 0 ? 'border-t border-border' : ''}`}
        >
          <p className="text-body-sm">{field.label}</p>
          <Select
            value={field.exposure}
            onValueChange={(value) => updateExposure(field.key, value as Exposure)}
          >
            <SelectTrigger className="h-9 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {field.choices.map((choice) => (
                <SelectItem key={choice} value={choice}>
                  {labels[choice]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
    </section>
  )
}

function PrivacySwitch({
  label,
  checked,
  onChange,
  divided = false,
}: {
  label: string
  checked: boolean
  onChange: (value: boolean) => void
  divided?: boolean
}) {
  return (
    <div
      className={`${divided ? 'border-t border-border' : ''} flex items-center justify-between gap-4 px-5 py-4`}
    >
      <p className="text-body-sm">{label}</p>
      <Switch checked={checked} onCheckedChange={onChange} aria-label={label} />
    </div>
  )
}
